import { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { Building2, MessageSquare, Users, DollarSign, TrendingUp, Activity } from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from 'chart.js';
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.getDashboard().then(({ data }) => setData(data.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex-center" style={{ height: '60vh' }}><div className="spinner" /></div>;

  const { stats, messagesByMonth, planDistribution, recentMerchants } = data || {};

  const chartData = {
    labels: messagesByMonth?.map(d => `${d._id.year}-${String(d._id.month).padStart(2, '0')}`) || [],
    datasets: [{ label: 'Messages', data: messagesByMonth?.map(d => d.count) || [], backgroundColor: 'rgba(99,102,241,0.5)', borderRadius: 4 }],
  };

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Admin Dashboard</h1><p className="page-subtitle">Platform-wide overview</p></div>
        <Link to="/admin/merchants" className="btn btn-primary"><Building2 size={15} /> Manage Merchants</Link>
      </div>
      <div className="page-body">
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          {[
            { icon: <Building2 size={18} />, bg: 'rgba(99,102,241,0.10)', color: 'var(--primary)', label: 'Total Merchants', value: stats?.totalMerchants || 0 },
            { icon: <Activity size={18} />, bg: 'rgba(34,197,94,0.10)', color: 'var(--green)', label: 'Active Merchants', value: stats?.activeMerchants || 0 },
            { icon: <MessageSquare size={18} />, bg: 'rgba(99,102,241,0.10)', color: 'var(--primary)', label: 'Total Messages', value: (stats?.totalMessages || 0).toLocaleString() },
            { icon: <Users size={18} />, bg: 'rgba(249,115,22,0.10)', color: 'var(--orange)', label: 'Total Customers', value: (stats?.totalCustomers || 0).toLocaleString() },
            { icon: <DollarSign size={18} />, bg: 'rgba(34,197,94,0.10)', color: 'var(--green)', label: 'Total Revenue', value: `$${((stats?.totalRevenue || 0) / 100).toLocaleString()}` },
          ].map((s, i) => (
            <div key={i} className="stat-card">
              <div className="stat-icon" style={{ background: s.bg, color: s.color }}>{s.icon}</div>
              <div className="stat-label">{s.label}</div>
              <div className="stat-value">{s.value}</div>
            </div>
          ))}
        </div>

        <div className="grid-2" style={{ gap: 16 }}>
          <div className="card">
            <div className="card-title" style={{ marginBottom: 14 }}>Messages (6 months)</div>
            {messagesByMonth?.length ? <Bar data={chartData} options={{ responsive: true, plugins: { legend: { display: false } }, scales: { x: { ticks: { color: 'var(--text-muted)', font: { size: 10 } }, grid: { color: 'rgba(0,0,0,0.06)' } }, y: { ticks: { color: 'var(--text-muted)', font: { size: 10 } }, grid: { color: 'rgba(0,0,0,0.06)' } } } }} />
              : <div className="empty-state" style={{ padding: '40px 0' }}><TrendingUp size={28} /><h3>No data yet</h3></div>}
          </div>

          <div className="card">
            <div className="card-title" style={{ marginBottom: 14 }}>Recent Merchants</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {recentMerchants?.map(m => (
                <div key={m._id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="avatar avatar-sm">{(m.businessName || '?')[0]}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>{m.businessName}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{m.email}</div>
                  </div>
                  <span className={`badge ${m.status === 'active' ? 'badge-green' : 'badge-red'}`}>{m.status}</span>
                  <Link to={`/admin/merchants/${m._id}`} className="btn btn-ghost btn-sm">View</Link>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Plan distribution */}
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-title" style={{ marginBottom: 14 }}>Merchants by Plan</div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {planDistribution?.map(p => (
              <div key={p._id} style={{ textAlign: 'center', padding: '12px 20px', background: 'var(--bg)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 800 }}>{p.count}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'capitalize', marginTop: 2 }}>{p._id || 'free'}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
